<template>
    <div class="container mb-8">
        <ais-instant-search
            :search-client="searchClient"
            :index-name="selectedIndex"
            :routing="routing"
        >
            <ais-configure
                :hits-per-page.camel="12"
                :filters="`roam_deals_end >= ${currentTimestamp}`"
            />

            <div class="mb-12">
                <div class="flex flex-col justify-between h-auto sm:flex-row sm:h-12">
                    <div class="w-full h-full mb-6 mr-0 sm:3/5 sm:mr-4 sm:mb-0">
                        <ais-search-box
                            v-slot="{ currentRefinement, isSearchStalled, refine }"
                            class="h-full"
                        >
                            <div class="flex h-12 overflow-hidden border rounded border-grey-200">
                                <input
                                    class="flex-grow ml-8 mr-4 font-sans text-xs tracking-widest uppercase bg-transparent outline-none placeholder-grey-300"
                                    type="search"
                                    :value="currentRefinement"
                                    placeholder="Search here…"
                                    @input="refine($event.currentTarget.value)"
                                />

                                <img
                                    :hidden="!isSearchStalled"
                                    class="h-full"
                                    src="/themes/coralcoast/images/spinner.svg"
                                    alt=""
                                />

                                <span
                                    class="flex items-center justify-center w-12 text-white bg-teal-600"
                                >
                                    <!-- prettier-ignore -->
                                    <svg class="w-4 h-4 stroke-current" viewBox="0 0 20 20">
                                        <g fill="none" fill-rule="evenodd" stroke-width="1.5" transform="translate(2 2)">
                                            <path stroke-linecap="round" d="M16 16L11 11"/>
                                            <circle cx="6.5" cy="6.5" r="6.5"/>
                                        </g>
                                    </svg>
                                </span>
                            </div>
                        </ais-search-box>
                    </div>

                    <ais-menu-select
                        v-slot="{ items, canRefine, refine }"
                        attribute="roam_products_regions.lvl0"
                        class="h-12 sm:w-64"
                    >
                        <Select
                            :items="items"
                            :can-refine="canRefine"
                            :refine="refine"
                            placeholder="Everywhere"
                        />
                    </ais-menu-select>
                </div>
            </div>

            <Categories />

            <ais-current-refinements
                v-slot="{ items }"
                :included-attributes="['roam_products_categories.lvl0']"
            >
                <Dates
                    v-if="isEventsActive(items)"
                    attribute="roam_products_eventDates"
                    class="mt-8 mb-12"
                />

                <div v-else></div>
            </ais-current-refinements>

            <div class="mb-12 sm:mb-32">
                <ais-hits v-slot="{ items }" class="grid gap-5 grid-square deals-grid">
                    <Deal v-for="item in items" :key="item.objectID" :product="item" />
                </ais-hits>

                <ais-pagination
                    v-slot="{
                        currentRefinement,
                        pages,
                        isFirstPage,
                        isLastPage,
                        refine,
                        createURL
                    }"
                    :padding="2"
                >
                    <div class="flex justify-center mt-16 small text-grey-300">
                        <a
                            v-if="!isFirstPage"
                            class="flex items-center justify-center w-12 h-12"
                            :href="createURL(currentRefinement - 1)"
                            @click.prevent="refine(currentRefinement - 1)"
                        >
                            <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                <path
                                    fill="none"
                                    fill-rule="evenodd"
                                    stroke-linecap="round"
                                    stroke-width="1.5"
                                    d="M4.931 1.069L1.069 4.931m0 .138l3.862 3.862"
                                />
                            </svg>
                        </a>

                        <a
                            v-for="page in pages"
                            :key="page"
                            class="flex items-center justify-center w-12 h-12 mx-1 rounded-full hover:text-grey-500 hover:bg-white"
                            :class="{ 'text-white bg-teal-600': page === currentRefinement }"
                            :href="createURL(page)"
                            @click.prevent="refine(page)"
                        >
                            {{ page + 1 }}
                        </a>

                        <a
                            v-if="!isLastPage"
                            class="flex items-center justify-center w-12 h-12"
                            :href="createURL(currentRefinement + 1)"
                            @click.prevent="refine(currentRefinement + 1)"
                        >
                            <svg class="w-2 h-4 stroke-current" viewBox="0 0 6 10">
                                <path
                                    fill="none"
                                    fill-rule="evenodd"
                                    stroke-linecap="round"
                                    stroke-width="1.5"
                                    d="M1.069 8.931l3.862-3.862m0-.138L1.069 1.069"
                                />
                            </svg>
                        </a>
                    </div>
                </ais-pagination>
            </div>
        </ais-instant-search>
    </div>
</template>

<script>
import algoliasearch from 'algoliasearch/lite';
import { routing } from '@/routing';
import Deal from '@/components/Deal.vue';
import Categories from '@/components/Categories.vue';
import Dates from '@/components/Dates.vue';
import Select from '@/components/Select.vue';

export default {
    components: {
        Deal,
        Categories,
        Dates,
        Select
    },

    data() {
        const index = `deals_${this.$root.hub}`;

        return {
            searchClient: algoliasearch(window.algolia.id, window.algolia.key),
            selectedIndex: index,
            routing: routing(index),
            currentTimestamp: Math.floor(Date.now() / 1000)
        };
    },

    methods: {
        isEventsActive(refinements) {
            const category = refinements.find(
                (cat) => cat.attribute === 'roam_products_categories.lvl0'
            );

            if (category) return category.refinements.find((cat) => cat.value === 'Event');

            return false;
        }
    }
};
</script>

<style scoped>
.subitem-wrapper {
    &::before,
    &::after {
        margin: auto;
        content: '';
    }
}
</style>
